// Property setup DTOs — PRD §3.1–3.2, 08 §3.2–3.3.
import { z } from 'zod';
import { channelSettingsSchema } from './channel.js';
import { dateSchema, moneySchema, uuidSchema } from './common.js';
const code = z
    .string()
    .trim()
    .regex(/^[A-Z0-9_-]{1,16}$/, 'code: A-Z, 0-9, _ or -');
const amount4 = moneySchema.shape.amount;
const iso4217 = z.string().regex(/^[A-Z]{3}$/);
export const roomTypeInputSchema = z.object({
    code,
    nameAr: z.string().trim().min(1).max(80),
    nameEn: z.string().trim().min(1).max(80).nullable().optional(),
    descriptionAr: z.string().max(500).nullable().optional(),
    baseOccupancy: z.number().int().min(1).max(10),
    maxAdults: z.number().int().min(1).max(10),
    maxChildren: z.number().int().min(0).max(10).default(0),
    features: z.array(z.string().trim().min(1).max(32)).default([]),
    sortOrder: z.number().int().default(0),
    overbookingAllowance: z.number().int().min(0).max(50).default(0),
});
export const roomInputSchema = z.object({
    roomTypeId: uuidSchema,
    number: z.string().trim().min(1).max(16),
    building: z.string().trim().max(32).nullable().optional(),
    floor: z.string().trim().max(16).nullable().optional(),
    features: z.array(z.string().trim().min(1).max(32)).default([]),
    notes: z.string().max(500).nullable().optional(),
});
export const roomStatusPatchSchema = z
    .object({
    housekeepingStatus: z.enum(['dirty', 'clean', 'inspected']).optional(),
    serviceStatus: z.enum(['in_service', 'out_of_order', 'out_of_service']).optional(),
    reason: z.string().max(200).optional(),
})
    .refine((v) => v.housekeepingStatus || v.serviceStatus, 'nothing to change');
export const ratePlanInputSchema = z.object({
    code,
    nameAr: z.string().trim().min(1).max(80),
    nameEn: z.string().trim().min(1).max(80).nullable().optional(),
    currency: iso4217,
    kind: z.enum(['public', 'corporate', 'package']).default('public'),
    taxInclusive: z.boolean().default(true),
    inclusions: z
        .array(z.object({
        code: z.string(),
        nameAr: z.string(),
        per: z.enum(['person_night', 'room_night', 'stay']),
    }))
        .default([]),
    validFrom: dateSchema.nullable().optional(),
    validTo: dateSchema.nullable().optional(),
    isActive: z.boolean().default(true),
});
export const rateRuleInputSchema = z
    .object({
    ratePlanId: uuidSchema,
    roomTypeId: uuidSchema,
    validFrom: dateSchema,
    validTo: dateSchema,
    dowMask: z.number().int().min(1).max(127).default(127),
    basePrice: amount4,
    extraAdult: amount4.default('0.0000'),
    extraChild: amount4.default('0.0000'),
})
    .refine((v) => v.validTo > v.validFrom, 'validTo must be after validFrom');
export const taxInputSchema = z.object({
    code,
    nameAr: z.string().trim().min(1).max(80),
    nameEn: z.string().trim().min(1).max(80).nullable().optional(),
    kind: z.enum(['percentage', 'fixed_per_night', 'fixed_per_stay', 'service_charge']),
    rate: z.string().regex(/^\d+(\.\d{1,6})?$/),
    isInclusive: z.boolean().default(true),
    applyOrder: z.number().int().min(1).max(20).default(1),
    effectiveFrom: dateSchema,
    effectiveTo: dateSchema.nullable().optional(),
});
export const chargeCodeInputSchema = z.object({
    code,
    nameAr: z.string().trim().min(1).max(80),
    nameEn: z.string().trim().min(1).max(80).nullable().optional(),
    revenueGroup: z.enum([
        'room',
        'f_and_b',
        'laundry',
        'minibar',
        'phone',
        'misc',
        'package',
        'fee',
        'system',
    ]),
    defaultPrice: amount4.nullable().optional(),
    defaultCurrency: iso4217.nullable().optional(),
    priceIncludesTax: z.boolean().default(true),
    allowManualPosting: z.boolean().default(true),
    taxIds: z.array(uuidSchema).default([]),
});
export const exchangeRateInputSchema = z
    .object({
    fromCurrency: iso4217,
    toCurrency: iso4217,
    rate: z
        .string()
        .regex(/^\d+(\.\d{1,10})?$/)
        .refine((r) => Number(r) > 0, 'rate must be > 0'),
    effectiveFrom: z.iso.datetime().optional(),
})
    .refine((v) => v.fromCurrency !== v.toCurrency, 'currencies must differ');
export const propertySettingsPatchSchema = z.object({
    nameAr: z.string().trim().min(1).max(120).optional(),
    nameEn: z.string().trim().min(1).max(120).nullable().optional(),
    settings: z
        .object({
        checkInTime: z
            .string()
            .regex(/^\d{2}:\d{2}$/)
            .optional(),
        checkOutTime: z
            .string()
            .regex(/^\d{2}:\d{2}$/)
            .optional(),
        features: z.array(z.string().trim().min(1).max(32)).optional(),
        tentativeHoldHours: z.number().int().min(1).max(720).optional(),
        overbookingPropertyAllowance: z.number().int().min(0).optional(),
        /** FCST BR-FCST-02 (v1.1) */
        forecast: z
            .object({
            tentativeWeight: z.number().min(0).max(1),
            weeks: z.number().int().min(1).max(26),
        })
            .optional(),
        /** ADR-0022 channel layer */
        channels: channelSettingsSchema.optional(),
        /** v1.1 owner summary via WhatsApp: digits with country code (963…); null = pick the chat by hand */
        ownerWhatsapp: z
            .string()
            .regex(/^[0-9]{8,15}$/)
            .nullable()
            .optional(),
    })
        .partial()
        .optional(),
});
/** Quote request for the rate simulator (REQ-RATE-05) and for reservation creation. */
export const quoteRequestSchema = z
    .object({
    ratePlanId: uuidSchema,
    roomTypeId: uuidSchema,
    arrival: dateSchema,
    departure: dateSchema,
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10).default(0),
})
    .refine((v) => v.departure > v.arrival, 'departure must be after arrival');
//# sourceMappingURL=property.js.map